import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/userService';
import EmployeesAdmin from './EmployeesAdmin';
import EmployeeSalaryManager from '../components/employee/EmployeeSalaryManager';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const EmployeeEmployeesView = ({ user }) => {
  const [filters, setFilters] = useState({
    employeeId: '',
    name: '',
    email: '',
    merchantId: '',
    merchantName: '',
    startDate: '',
    endDate: ''
  });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [profileRows, setProfileRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    userService
      .getAll()
      .then((users) => {
        const rows = users.map((item) => ({
          id: item.id,
          employeeId: item.employeeId || item.id,
          name: [item.firstName, item.middleName, item.lastName].filter(Boolean).join(' ') || item.username,
          firstName: item.firstName,
          middleName: item.middleName,
          lastName: item.lastName,
          email: item.email,
          merchantId: item.orgId ? `Org ${item.orgId}` : '',
          merchantName: item.orgName || '',
          orgId: item.orgId,
          role: item.designation || item.roles || '',
          designation: item.designation || item.role || '',
          department: item.department || '',
          userRoles: item.userRoles || (item.roles ? [item.roles] : []),
          mobile: item.mobile || '',
          phone: item.phone || '',
          emergencyContactName: '',
          emergencyContactMobile: '',
          joinedOn: item.joiningDate || '',
          joiningDate: item.joiningDate || '',
          linkedin: '',
          enabled: item.enabled !== false,
          hierarchy: {
            manager: item.managerUserId ? `User ${item.managerUserId}` : '-',
            hrbp: '-',
            skipLevel: '-',
          },
        }));
        setProfileRows(rows);
        setSelectedEmployeeId(rows[0]?.id || null);
      })
      .catch((apiError) => setError(apiError.userMessage || 'Unable to load employees.'))
      .finally(() => setLoading(false));
  }, []);

  const filteredEmployees = useMemo(
    () =>
      profileRows.filter((employee) => {
        const byEmployeeId = String(employee.id).toLowerCase().includes(filters.employeeId.toLowerCase());
        const byName = String(employee.name || '').toLowerCase().includes(filters.name.toLowerCase());
        const byEmail = String(employee.email || '').toLowerCase().includes(filters.email.toLowerCase());
        const byMerchantId = employee.merchantId.toLowerCase().includes(filters.merchantId.toLowerCase());
        const byMerchantName = employee.merchantName.toLowerCase().includes(filters.merchantName.toLowerCase());
        const byStart = !filters.startDate || employee.joinedOn >= filters.startDate;
        const byEnd = !filters.endDate || employee.joinedOn <= filters.endDate;
        return byEmployeeId && byName && byEmail && byMerchantId && byMerchantName && byStart && byEnd;
      }),
    [filters, profileRows]
  );

  const selectedEmployee = profileRows.find((employee) => employee.id === selectedEmployeeId) || profileRows[0];
  if (loading) {
    return <p className="text-gray-500">Loading employees...</p>;
  }
  if (!selectedEmployee) {
    return <p className="text-red-600">{error || 'No employees found.'}</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Employees</h1>
        <p className="text-gray-500 mt-1">Employee detail and hierarchy</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input placeholder="Employee id" value={filters.employeeId} onChange={(e) => setFilters((p) => ({ ...p, employeeId: e.target.value }))} />
          <Input placeholder="Name" value={filters.name} onChange={(e) => setFilters((p) => ({ ...p, name: e.target.value }))} />
          <Input placeholder="Email" value={filters.email} onChange={(e) => setFilters((p) => ({ ...p, email: e.target.value }))} />
          <Input placeholder="Merchant id" value={filters.merchantId} onChange={(e) => setFilters((p) => ({ ...p, merchantId: e.target.value }))} />
          <Input placeholder="Merchant name" value={filters.merchantName} onChange={(e) => setFilters((p) => ({ ...p, merchantName: e.target.value }))} />
          <Input type="date" value={filters.startDate} onChange={(e) => setFilters((p) => ({ ...p, startDate: e.target.value }))} />
          <Input type="date" value={filters.endDate} onChange={(e) => setFilters((p) => ({ ...p, endDate: e.target.value }))} />
          <Button variant="outline" onClick={() => setFilters({ employeeId: '', name: '', email: '', merchantId: '', merchantName: '', startDate: '', endDate: '' })}>
            Reset Filters
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)] gap-6">
        <Card className="h-fit shadow-sm border-gray-200">
          <CardHeader className="pb-3 border-b">
            <CardTitle className="text-base">Result Grid</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="border rounded-lg max-h-[680px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Org / Merchant</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.map((employee) => (
                    <TableRow
                      key={employee.id}
                      className={`cursor-pointer transition-colors ${selectedEmployee.id === employee.id ? 'bg-blue-50/80 font-medium' : 'hover:bg-gray-50'}`}
                      onClick={() => setSelectedEmployeeId(employee.id)}
                    >
                      <TableCell>
                        <p className="font-semibold text-gray-900">{employee.name}</p>
                        <p className="text-xs text-gray-500 font-mono">{employee.employeeId || employee.id}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[160px]">{employee.email}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-xs text-gray-700">{employee.merchantName || 'Tech Apurva'}</p>
                        <p className="text-[11px] text-gray-400 font-mono">{employee.merchantId || '-'}</p>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="min-w-0">
          {selectedEmployee && (
            <EmployeeSalaryManager
              employee={selectedEmployee}
              onEmployeeUpdate={(updated) => {
                setProfileRows((prev) =>
                  prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r))
                );
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const Employees = () => {
  const { isEmployee, user } = useAuth();
  if (!isEmployee) {
    return <EmployeesAdmin />;
  }
  return <EmployeeEmployeesView user={user} />;
};

export default Employees;
